<template>
  <div class="video-thumb" :class="{ 'is-processing': isProcessing, 'is-failed': isFailed, 'is-editable-draft': isOwnerDraft }">
    <div class="thumbnail-wrapper" @mouseenter="onHover" @mouseleave="onLeave">
        <router-link :to="thumbnailLink" :event="isBlocked ? '' : 'click'" class="thumbnail-link">
            <div class="video-thumbnail">
                <img
                    :src="thumbnailSrc"
                    :alt="video.titleEN || video.titleTA"
                    class="thumbnail-img"
                    :class="{ 'thumbnail-hidden': showPreview }"
                    @error="handleImageError"
                />
                <img
                    v-if="showPreview && previewLoaded"
                    :src="previewURL"
                    class="thumbnail-img preview-gif"
                    alt="Preview"
                />
                <!-- Processing overlay -->
                <div class="processing-overlay" v-if="showStatusOverlay">
                    <div class="processing-spinner" :class="{ failed: isFailed }">
                        <vue-feather v-if="isFailed" type="alert-triangle" size="34" stroke-width="2.2"></vue-feather>
                        <vue-feather v-else type="loader" size="32" animation="spin"></vue-feather>
                    </div>
                    <span class="processing-label">{{ statusLabel }}</span>
                    <span class="processing-estimate" v-if="statusDetail">{{ statusDetail }}</span>
                </div>
                <!-- Hover overlay with play button -->
                <div class="thumbnail-overlay" :class="{ 'preview-active': showPreview }" v-if="!showStatusOverlay">
                    <div class="play-button">
                        <vue-feather type="play" size="32" fill="currentColor"></vue-feather>
                    </div>
                </div>
            </div>
        </router-link>
        <span class="draft-corner-badge" v-if="draftBadge">{{ draftBadge }}</span>
        <div class="duration" v-if="!showStatusOverlay">{{ formattedDuration(video.duration) }}</div>
        <div class="duration processing-badge" v-if="showStatusOverlay">
            <vue-feather v-if="isFailed" type="alert-triangle" size="10"></vue-feather>
            <vue-feather v-else type="loader" size="10" animation="spin"></vue-feather>
            {{ badgeLabel }}
        </div>
        <!-- Watch later button (optional) -->
        <button class="watch-later-btn" @click.prevent="addToWatchLater" title="Watch later" v-if="!showStatusOverlay">
            <vue-feather type="clock" size="16"></vue-feather>
        </button>
    </div>
    <div class="video-info">
        <div class="user-image">
            <router-link class="avatar-link" :to="`/@${videoOwner.username}`">
                <img class="profile-image" :src="imageForUserId(videoOwner)" :alt="displayName(videoOwner)" @error="handleAvatarError" />
            </router-link>
        </div>

        <div class="owner-details">
            <router-link class="item-title" :to="thumbnailLink">
                {{ decodeHtml(currentLanguage === 'EN' ? (video.titleEN || video.titleTA) : (video.titleTA || video.titleEN)) }}
            </router-link>
            <div class="channel-row">
                <router-link class="owner-name" :to="`/@${videoOwner.username}`">
                    {{ displayName(videoOwner) }}
                </router-link>
                <vue-feather type="check-circle" stroke-width="3" class="verified-icon" v-if="videoOwner.status === 'verified'" />
            </div>
            <div class="video-meta">
                <span class="meta-item">
                    <span class="meta-play-icon">
                        <vue-feather type="play" size="9" fill="currentColor" stroke-width="2.5"></vue-feather>
                    </span>
                    {{ formatViews(video.viewsCount) }}
                </span>
                <span class="meta-separator">•</span>
                <span class="meta-item">
                    <vue-feather type="clock" size="12" stroke-width="2" class="meta-icon"></vue-feather>
                    {{ timeAgo(Date.parse(video.createdAt)) }}
                </span>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import VueFeather from 'vue-feather';

export default {
    name: 'video-thumb',
    components: {
        VueFeather
    },
    props: {
        userImages: {
            type: Array,
            default() {
                return []
            }
        },
        video: {
            type: Object,
            default() {
                return null
            }
        }
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    data() {
        return {
            imageError: false,
            cdnDomain: 'https://cdn1.eelam.tv',
            showPreview: false,
            previewLoaded: false,
            hoverTimer: null,
            previewImg: null,
        }
    },
    computed: {
        currentLanguage() {
            return this.languageRef?.value || this.languageRef || 'EN';
        },
        isPendingReview() {
            return this.video?.publishState === 'pending' &&
                ['ready', 'processingReady', 'thumbsReady'].includes(this.video?.status);
        },
        showStatusOverlay() {
            return this.isProcessing || this.isPendingReview;
        },
        isOwnerDraft() {
            // The current user's own not-yet-public video (processing / pending / failed).
            return !!this.video?.isOwnerVisibleDraft;
        },
        isBlocked() {
            // Owners can always open their own draft to edit it, even while processing.
            return this.isProcessing && !this.isPendingReview && !this.isOwnerDraft;
        },
        thumbnailLink() {
            if (!this.video?.id) return '';
            // Owner's own draft (processing / pending / failed) → open the edit page.
            if (this.isOwnerDraft && (this.isProcessing || this.isPendingReview)) {
                return `/finish/${this.video.id}`;
            }
            return this.isBlocked ? '' : `/watch/${this.video.id}`;
        },
        isProcessing() {
            return this.video && this.video.status && !['ready', 'processingReady'].includes(this.video.status);
        },
        isFailed() {
            return this.video?.status === 'failed';
        },
        // Label for a playable-but-unpublished video (draft/private/rejected). Pending
        // and still-processing states are already covered by the status overlay above.
        draftBadge() {
            const ps = this.video?.publishState;
            if (!ps || ps === 'published' || ps === 'pending') return null;
            if (this.isProcessing || this.isPendingReview) return null;
            const labels = { draft: 'Draft', private: 'Private', rejected: 'Rejected' };
            return labels[ps] || ps;
        },
        statusLabel() {
            if (this.isFailed) return 'Processing failed';
            if (this.isPendingReview) return 'Pending review';
            return 'Processing';
        },
        badgeLabel() {
            if (this.isFailed) return 'Failed';
            if (this.isPendingReview) return 'Pending';
            return 'Processing';
        },
        statusDetail() {
            if (this.isFailed) {
                return this.isOwnerDraft ? 'Tap to edit or replace' : (this.video?.errorMessage ? 'Needs attention' : '');
            }
            if (this.isPendingReview) {
                return 'Finish details or wait for approval';
            }
            if (this.isOwnerDraft) {
                return this.estimatedTime ? `${this.estimatedTime} · Tap to edit` : 'Tap to edit details';
            }
            return this.estimatedTime;
        },
        estimatedTime() {
            if (!this.isProcessing || this.isFailed || !this.video.duration) return '';
            const durationMin = this.video.duration / 60;
            // ~2 min overhead + ~2.5x realtime for encoding multiple HLS variants
            const estMinutes = Math.ceil(2 + durationMin * 2.5);
            if (estMinutes < 60) return `~${estMinutes} min`;
            const hours = Math.floor(estMinutes / 60);
            const mins = estMinutes % 60;
            return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`;
        },
        imageURL() {
            if (!this.video || !this.video.id) {
                return '/video-placeholder.svg';
            }
            // Use thumbnailUrl from legacy data if available and not empty
            if (this.video.thumbnailUrl && this.video.thumbnailUrl.trim()) {
                if (this.video.thumbnailUrl.includes('https://')) {
                    return this.video.thumbnailUrl;
                }
                let imageURL = `https://cdn.eelam.tv/${this.video.thumbnailUrl}`;
                return imageURL;
            }
            // Fallback to CDN URL
            const imageIndex = this.video.imageName || '0';
            let imageURL = `${this.cdnDomain}/public/thumbs/${this.video.id}_${imageIndex}.jpeg`;
            return imageURL;
        },
        thumbnailSrc() {
            return this.imageError ? '/video-placeholder.svg' : this.imageURL;
        },
        previewURL() {
            if (!this.video || !this.video.id) return '';
            return `${this.cdnDomain}/public/thumbs/${this.video.id}_preview.gif`;
        },
        videoOwner() {
            // Return owner if exists, otherwise return dummy owner data
            if (this.video.owner && this.video.owner.id) {
                return this.video.owner;
            }
            // Create dummy owner for videos without an owner
            return {
                id: 'eelam-tv',
                username: 'eelam-tv',
                displayName: 'Eelam TV',
                firstName: 'Eelam',
                lastName: 'TV',
                status: 'unverified',
                emailVerified: false
            };
        }
    },
    created() {
    },
    watch: {
        'video.id': {
            handler() {
                // Reset image error state when video changes
                this.imageError = false;
            }
        }
    },
    methods: {
        imageForUserId(owner) {
            if (owner && owner.legacyAvatar) {
                if (owner.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${owner.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${owner.legacyAvatar}`;
            }
            if (!owner?.id) return '/avatar-placeholder.svg';
            const userId = owner.id;
            return `${this.cdnDomain}/public/images/user/${userId}/avatar.png`;
        },
        displayName(user) {
            const fullName = [user?.firstName, user?.lastName]
                .filter(Boolean)
                .join(' ')
                .trim();
            return fullName || user?.displayName || user?.username || 'Unknown User';
        },
        formatViews(count) {
            const label = this.currentLanguage === 'TA'
                ? this.tamilPlural(count || 0, 'பார்வை', 'பார்வைகள்')
                : 'plays';
            if (!count) return `0 ${label}`;
            if (count >= 1000000) {
                return (count / 1000000).toFixed(1) + `M ${label}`;
            } else if (count >= 1000) {
                return (count / 1000).toFixed(1) + `K ${label}`;
            }
            return count + ` ${label}`;
        },
        timeAgo(date) {
            const now = new Date();
            const diffInSeconds = Math.floor((now - date) / 1000);
            const ta = this.currentLanguage === 'TA';

            if (diffInSeconds < 60) {
                return ta ? 'இப்போது' : 'just now';
            }
            if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                return ta ? `${minutes} ${this.tamilPlural(minutes, 'நிமிடம்', 'நிமிடங்கள்')} முன்` : `${minutes}m ago`;
            }
            if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                return ta ? `${hours} ${this.tamilPlural(hours, 'மணி', 'மணி')} முன்` : `${hours}h ago`;
            }
            if (diffInSeconds < 2592000) {
                const days = Math.floor(diffInSeconds / 86400);
                return ta ? `${days} ${this.tamilPlural(days, 'நாள்', 'நாட்கள்')} முன்` : `${days}d ago`;
            }
            if (diffInSeconds < 31536000) {
                const months = Math.floor(diffInSeconds / 2592000);
                return ta ? `${months} ${this.tamilPlural(months, 'மாதம்', 'மாதம்')} முன்` : `${months}mo ago`;
            }
            const years = Math.floor(diffInSeconds / 31536000);
            return ta ? `${years} ${this.tamilPlural(years, 'ஆண்டு', 'ஆண்டு')} முன்` : `${years}y ago`;
        },
        tamilPlural(count, singular, plural) {
            return Number(count) === 1 ? singular : plural;
        },
        addToWatchLater() {
            // Placeholder for watch later functionality
            console.log('Add to watch later:', this.video.id);
        },
        handleImageError() {
            console.log('Image failed to load for video:', this.video.id, 'URL:', this.imageURL);
            this.imageError = true;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        onHover() {
            // Delay before loading GIF to avoid unnecessary requests on quick mouse passes
            this.hoverTimer = setTimeout(() => {
                if (!this.previewLoaded) {
                    // Preload the GIF
                    this.previewImg = new Image();
                    this.previewImg.onload = () => {
                        this.previewLoaded = true;
                        this.showPreview = true;
                    };
                    this.previewImg.onerror = () => {
                        // GIF doesn't exist, just keep showing thumbnail
                        this.previewLoaded = false;
                    };
                    this.previewImg.src = this.previewURL;
                } else {
                    this.showPreview = true;
                }
            }, 500);
        },
        onLeave() {
            clearTimeout(this.hoverTimer);
            this.showPreview = false;
        }
    },
    beforeUnmount() {
        clearTimeout(this.hoverTimer);
    }
}
</script>

<style scoped>
.video-thumb {
    position: relative;
    transition: transform 0.2s ease;
}

.video-thumb:hover {
    transform: translateY(-4px);
}

/* Thumbnail wrapper */
.thumbnail-wrapper {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px var(--shadow-color);
    transition: box-shadow 0.3s ease;
}

.video-thumb:hover .thumbnail-wrapper {
    box-shadow: 0 8px 24px var(--shadow-color);
}

.draft-corner-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;
    background: #6b7280;
    color: #fff;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.thumbnail-link {
    display: block;
}

.video-thumbnail {
    width: 100%;
    aspect-ratio: 16 / 9;
    background-color: var(--bg-tertiary);
    transition: transform 0.4s ease;
    position: relative;
    overflow: hidden;
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.3s ease;
}

.thumbnail-img.thumbnail-hidden {
    opacity: 0;
}

.preview-gif {
    z-index: 1;
    opacity: 1;
}

.thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
}

.video-thumb:not(.is-processing):hover .video-thumbnail {
    transform: scale(1.05);
}

/* Processing overlay */
.processing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    z-index: 2;
}

.is-failed .processing-overlay {
    background: rgba(35, 8, 8, 0.72);
}

.processing-spinner {
    color: white;
    opacity: 0.9;
}

.processing-spinner.failed {
    color: #ffb3a8;
}

.processing-label {
    color: white;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    opacity: 0.9;
}

.processing-estimate {
    color: white;
    font-size: 11px;
    opacity: 0.7;
}

.processing-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0, 0, 0, 0.85) !important;
}

.is-failed .processing-badge {
    background: #b42318 !important;
    color: white;
}

.is-processing {
    opacity: 0.8;
    pointer-events: auto;
}

.is-processing.is-failed {
    opacity: 1;
}

.is-processing .thumbnail-wrapper {
    cursor: default;
}

.is-processing .thumbnail-link {
    cursor: default;
}

/* Owner drafts stay clickable (they open the edit page) even while processing. */
.is-editable-draft.is-processing .thumbnail-wrapper,
.is-editable-draft.is-processing .thumbnail-link,
.is-editable-draft .item-title {
    cursor: pointer;
}

.is-processing:hover {
    transform: none;
}

/* Overlay with play button */
.thumbnail-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.6) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.video-thumb:hover .thumbnail-overlay:not(.preview-active) {
    opacity: 1;
}

.thumbnail-overlay.preview-active {
    background: none;
}

.thumbnail-overlay.preview-active .play-button {
    display: none;
}

.play-button {
    width: 56px;
    height: 56px;
    padding-left: 5px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent-primary);
    transform: scale(0.8);
    opacity: 0;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.play-button svg {
    margin-left: 4px;
}

.video-thumb:hover .play-button {
    transform: scale(1);
    opacity: 1;
}

.play-button:hover {
    transform: scale(1.1) !important;
    background: white;
}

/* Duration badge */
.duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    z-index: 2;
    transition: all 0.2s ease;
}

.video-thumb:hover .duration {
    background: var(--accent-primary);
}

/* Watch later button */
.watch-later-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    background: rgba(0, 0, 0, 0.7);
    border: none;
    border-radius: 4px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: translateY(-8px);
    transition: all 0.3s ease;
    z-index: 3;
}

.video-thumb:hover .watch-later-btn {
    opacity: 1;
    transform: translateY(0);
}

.watch-later-btn:hover {
    background: var(--accent-primary);
    transform: scale(1.1);
}

/* Video info section */
.video-info {
    display: flex;
    padding: 12px 4px;
    gap: 12px;
}

.user-image {
    flex-shrink: 0;
}

.avatar-link {
    display: block;
}

.profile-image {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: block;
    object-fit: cover;
    background-color: var(--bg-tertiary);
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.avatar-link:hover .profile-image {
    border-color: var(--accent-primary);
    transform: scale(1.05);
}

.owner-details {
    flex: 1;
    min-width: 0;
}

/* Title */
.item-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    text-decoration: none;
    transition: color 0.2s ease;
}

.item-title:hover {
    color: var(--accent-primary);
}

/* Channel row */
.channel-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
}

.owner-name {
    color: var(--text-tertiary);
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.2s ease;
}

.owner-name:hover {
    color: var(--text-primary);
}

.verified-icon {
    width: 14px;
    height: 14px;
    color: var(--accent-primary);
}

/* Video meta */
.video-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-tertiary);
    font-size: 12px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 3px;
    min-width: 0;
    flex-shrink: 1;
    overflow: hidden;
    text-overflow: ellipsis;
}

.meta-icon {
    opacity: 0.7;
}

.meta-play-icon {
    width: 12px;
    height: 12px;
    flex: 0 0 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--text-tertiary);
    line-height: 0;
}

.meta-play-icon svg {
    display: block;
}

.meta-separator {
    color: var(--text-tertiary);
    opacity: 0.5;
    flex: 0 0 auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .video-thumb:hover {
        transform: none;
    }

    .thumbnail-overlay {
        opacity: 0 !important;
    }

    .watch-later-btn {
        opacity: 1;
        transform: translateY(0);
    }

    .profile-image {
        width: 32px;
        height: 32px;
    }

    .item-title {
        font-size: 13px;
    }

    .owner-name {
        font-size: 12px;
    }
}
</style>
